/**
 * ObjetosContrato Component
 * Gestion de objetos de contrato para FUEC
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUser } from "../../../UserContext";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Tooltip,
  Popconfirm,
  Typography,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ProfileOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  fetchObjetosContrato,
  createObjetoContrato,
  updateObjetoContrato,
  toggleObjetoContrato,
  deleteObjetoContrato,
} from "./api";

const { TextArea } = Input;
const { Text } = Typography;

const ObjetosContrato = () => {
  const [objetos, setObjetos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingObjeto, setEditingObjeto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const { user: currentUser } = useUser();

  // Verificación de permisos
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === "FUEC");
      if (!module || module.estado !== "disponible") return false;
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "FUEC" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  const canManage = hasPermission("GESTIONAR_OBJETOS_CONTRATO");

  // Cargar objetos
  const loadObjetos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchObjetosContrato({ solo_activos: false });
      setObjetos(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error("Error cargando objetos de contrato:", error);
      message.error("Error al cargar objetos de contrato");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadObjetos();
  }, [loadObjetos]);

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingObjeto(null);
    form.resetFields();
    form.setFieldsValue({ activo: true });
    setModalVisible(true);
  }, [form]);

  const handleEdit = useCallback(
    (record) => {
      setEditingObjeto(record);
      form.setFieldsValue({
        objeto_contrato: record.objeto_contrato,
        activo: record.activo,
      });
      setModalVisible(true);
    },
    [form]
  );

  const handleToggle = useCallback(
    async (record) => {
      try {
        await toggleObjetoContrato(record.id);
        message.success(
          record.activo
            ? "Objeto de contrato desactivado"
            : "Objeto de contrato activado"
        );
        loadObjetos();
      } catch (error) {
        console.error("Error cambiando estado:", error);
        message.error("Error al cambiar estado");
      }
    },
    [loadObjetos]
  );

  const handleDelete = useCallback(
    async (record) => {
      try {
        await deleteObjetoContrato(record.id);
        message.success("Objeto de contrato eliminado");
        loadObjetos();
      } catch (error) {
        console.error("Error eliminando:", error);
        const errorMsg =
          error.response?.data?.error || "Error al eliminar objeto de contrato";
        message.error(errorMsg);
      }
    },
    [loadObjetos]
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingObjeto) {
        await updateObjetoContrato(editingObjeto.id, values);
        message.success("Objeto de contrato actualizado");
      } else {
        await createObjetoContrato(values);
        message.success("Objeto de contrato creado");
      }

      setModalVisible(false);
      loadObjetos();
    } catch (error) {
      if (error.errorFields) {
        message.error("Por favor complete los campos requeridos");
      } else {
        console.error("Error guardando:", error);
        const errorMsg =
          error.response?.data?.error || "Error al guardar objeto de contrato";
        message.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Columnas de la tabla
  const columns = useMemo(
    () => [
      {
        title: "Objeto del Contrato",
        dataIndex: "objeto_contrato",
        key: "objeto_contrato",
        render: (text) => (
          <Tooltip title={text}>
            <Text ellipsis style={{ maxWidth: 600 }}>
              {text}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: "Estado",
        dataIndex: "activo",
        key: "activo",
        width: 100,
        align: "center",
        render: (activo) =>
          activo ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Activo
            </Tag>
          ) : (
            <Tag color="default" icon={<CloseCircleOutlined />}>
              Inactivo
            </Tag>
          ),
      },
      {
        title: "FUECs",
        dataIndex: "fuecs_count",
        key: "fuecs_count",
        width: 80,
        align: "center",
        render: (count) => (
          <Tag color={count > 0 ? "blue" : "default"}>{count || 0}</Tag>
        ),
      },
      {
        title: "Acciones",
        key: "acciones",
        width: 150,
        align: "center",
        render: (_, record) => (
          <Space>
            {canManage ? (
              <>
                <Tooltip title="Editar">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>
                <Tooltip title={record.activo ? "Desactivar" : "Activar"}>
                  <Button
                    type="text"
                    icon={
                      record.activo ? (
                        <CloseCircleOutlined />
                      ) : (
                        <CheckCircleOutlined />
                      )
                    }
                    onClick={() => handleToggle(record)}
                  />
                </Tooltip>
                <Popconfirm
                  title="Eliminar objeto de contrato"
                  description="Esta accion no se puede deshacer. Solo se puede eliminar si no tiene FUECs asociados."
                  onConfirm={() => handleDelete(record)}
                  okText="Si, eliminar"
                  cancelText="Cancelar"
                  icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
                >
                  <Tooltip title="Eliminar">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={record.fuecs_count > 0}
                    />
                  </Tooltip>
                </Popconfirm>
              </>
            ) : (
              <span style={{ color: "#888" }}>-</span>
            )}
          </Space>
        ),
      },
    ],
    [handleEdit, handleToggle, handleDelete, canManage]
  );

  return (
    <div className="objetos-contrato-container">
      <Card
        title={
          <Space>
            <ProfileOutlined />
            <span>Objetos de Contrato FUEC</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadObjetos}
              loading={loading}
            >
              Recargar
            </Button>
            {canManage && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Agregar Objeto
              </Button>
            )}
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Text type="secondary">
              Los objetos de contrato son los textos predefinidos que describen
              el proposito del servicio de transporte en el FUEC. Segun la
              normativa, existen 15 objetos contractuales validos para el
              servicio publico de transporte terrestre automotor especial.
            </Text>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={objetos}
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `${total} objeto(s)`,
          }}
          size="small"
        />
      </Card>

      {/* Modal de edicion/creacion */}
      <Modal
        title={
          <Space>
            <ProfileOutlined />
            {editingObjeto ? "Editar Objeto de Contrato" : "Nuevo Objeto de Contrato"}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        okText={editingObjeto ? "Actualizar" : "Crear"}
        cancelText="Cancelar"
        confirmLoading={submitting}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="objeto_contrato"
            label="Texto del Objeto de Contrato"
            rules={[
              { required: true, message: "El texto es requerido" },
              { max: 500, message: "Maximo 500 caracteres" },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Ingrese el texto completo del objeto de contrato..."
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="activo"
            label="Estado"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Activo"
              unCheckedChildren="Inactivo"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ObjetosContrato;
